import React, { useState } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Badge,
  Card,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  Home as HomeIcon,
  List as ListIcon,
  Dashboard as DashboardIcon,
} from "@mui/icons-material";
import { RootState } from "types";
import Gamelist from "./sections/Gamelist";
import CurrentGame from "./sections/PlayerDashboard/CurrentGame";
import PlayerDashboard from "./sections/PlayerDashboard";

interface TabPanelProps {
  children?: React.ReactNode;
  value: number;
  index: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`home-tabpanel-${index}`}
      aria-labelledby={`home-tab-${index}`}
      {...other}
    >
      {value === index && children}
    </Box>
  );
}

function a11yProps(index: number) {
  return {
    id: `home-tab-${index}`,
    "aria-controls": `home-tabpanel-${index}`,
  };
}

function Home() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const user = useSelector((state: RootState) => state.user.user);
  const [display, setDisplay] = useState(0); // Changed to numeric for tabs
  const requests = useSelector(
    (state: RootState) => state.requests.sentRequests,
  );

  const pendingConfirmation = () => {
    if (!requests) return 0;
    const keys = Object.keys(requests);
    const pending = keys.filter(
      (key) => requests[key]?.status === "confirmationPending",
    );
    return pending.length;
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setDisplay(newValue);
  };

  return (
    <Box sx={{ py: { xs: 2, md: 4 } }}>
      {/* Header Section */}
      <Box sx={{ mb: 4, textAlign: "center" }}>
        <Typography
          variant={isMobile ? "h4" : "h3"}
          component="h1"
          gutterBottom
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            color: "primary.main",
            fontWeight: 600,
          }}
        >
          <HomeIcon color="primary" fontSize={isMobile ? "large" : "inherit"} />
          Home
        </Typography>
        <Typography
          variant={isMobile ? "h5" : "h4"}
          color="text.primary"
          sx={{
            fontWeight: 400,
            mb: 1,
          }}
        >
          Welcome {user?.displayName || "User"}
        </Typography>
        <Typography
          variant="body1"
          color="textSecondary"
          sx={{ maxWidth: 600, mx: "auto" }}
        >
          Find and join poker games in your area, or manage your current
          sessions
        </Typography>
      </Box>

      {/* Current Game Section */}
      <Box sx={{ mb: 4 }}>
        <CurrentGame />
      </Box>

      {/* Navigation Tabs */}
      <Box>
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
          <Tabs
            value={display}
            onChange={handleTabChange}
            aria-label="home navigation tabs"
            variant="fullWidth"
            sx={{
              "& .MuiTab-root": {
                minHeight: 64,
                fontWeight: "bold",
                fontSize: "1rem",
                textTransform: "none",
              },
            }}
          >
            <Tab
              icon={<ListIcon />}
              label="Game List"
              {...a11yProps(0)}
              sx={{
                flexDirection: { xs: "column", sm: "row" },
                gap: 1,
              }}
            />
            <Tab
              icon={
                <Badge
                  badgeContent={pendingConfirmation()}
                  color="warning"
                  sx={{
                    "& .MuiBadge-badge": {
                      fontSize: "0.75rem",
                      minWidth: 20,
                      height: 20,
                    },
                  }}
                >
                  <DashboardIcon />
                </Badge>
              }
              label="Dashboard"
              {...a11yProps(1)}
              sx={{
                flexDirection: { xs: "column", sm: "row" },
                gap: 1,
              }}
            />
          </Tabs>
        </Box>

        {/* Tab Content */}
        <TabPanel value={display} index={0}>
          <Gamelist
            setDisplay={(newDisplay: string) => {
              if (newDisplay === "playerDash") setDisplay(1);
            }}
          />
        </TabPanel>
        <TabPanel value={display} index={1}>
          <PlayerDashboard
            setDisplay={(newDisplay: string) => {
              if (newDisplay === "gameslist") setDisplay(0);
            }}
          />
        </TabPanel>
      </Box>
    </Box>
  );
}

export default Home;
